import {ColorObjectStrategy} from "../../../../_types/ColorObjectStrategy";
import type {SceneObjectWithColorData} from "../../../../_types/SceneObjectWithColorData";
import type {WebGLRenderObject} from "../../../../_types/WebGLRenderObject";
import type {Color} from "../../../../helpers/customColor";
import { differenceEuclidean, nearest } from "../../../../helpers/customColor";
import {getRandomNumberBetween} from "../../../../helpers/getRandomNumberBetween";

// The random offset used to pick different colors based on position,
// this makes the colors based on position different each time.
const RANDOM_RENDER_OBJECT_OFFSET = 8;

export class PositionColorObjectStrategy implements ColorObjectStrategy {

    constructor(private maxColors?: number) {
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public color(
        object: WebGLRenderObject,
        sceneObject: SceneObjectWithColorData
    ): void {

        const localPosition = {
            x: object.x - sceneObject.x + getRandomNumberBetween(
                -RANDOM_RENDER_OBJECT_OFFSET,
                RANDOM_RENDER_OBJECT_OFFSET),
            y: object.y - sceneObject.y + getRandomNumberBetween(
                -RANDOM_RENDER_OBJECT_OFFSET,
                RANDOM_RENDER_OBJECT_OFFSET)
        };

        const mirroredRepeat = (coordinate: number, size: number): number => {
            const c = Math.abs(coordinate) % (size * 2);
            return c >= size ? size * 2 - c - 1 : c;
        };

        const pixelPosition = {
            x: mirroredRepeat(Math.floor(localPosition.x), sceneObject.width),
            y: mirroredRepeat(Math.floor(localPosition.y), sceneObject.height)
        };

        const imageDataPosition = {
            x: Math.floor((pixelPosition.x / sceneObject.width) * sceneObject.data.width),
            y: Math.floor((pixelPosition.y / sceneObject.height) * sceneObject.data.height)
        };

        const index = imageDataPosition.y * sceneObject.data.width + imageDataPosition.x;
        const rawColor = sceneObject.data.rawColors[index];

        const palette: Array<Color> | undefined = this.maxColors
            ? sceneObject.data.palettes[this.maxColors]
            : undefined;

        if (palette) {
            const nearestPaletteColor = nearest(palette, differenceEuclidean);
            object.color = nearestPaletteColor(rawColor, 1)[0];
        } else {
            object.color = rawColor;
        }
    }
}